// Single-note piano for fretboard tap feedback.
// Uses a sampled grand piano (Salamander, via the Tone.js CDN) so a tapped note
// has a clear, percussive attack that reads well by ear, distinct from the soft
// sustained drone used for chords. Loaded lazily on first tap.
//
// Deux samplers, un seul jeu d'échantillons décodés. Raison : `triggerRelease`
// arrête TOUTES les sources suivies de la hauteur demandée sur son sampler, et
// `triggerAttackRelease` (le tap du manche) en pose une. Sur un sampler unique,
// un tap sur la même note qu'une note de leçon en cours la coupe et la sort du
// suivi. Les deux samplers partagent les `ToneAudioBuffer` chargés une seule
// fois : même mémoire, même réverbération, même volume, suivis séparés.
//
// Ces buffers viennent de sampleBanks, le registre commun à toute la page : la
// banque d'accords et le bouton Ear du manche jouent les mêmes fichiers.

import * as Tone from 'tone';
import { createMixBus } from './playbackMix';
import { connectReverbSend, whenReverbReady } from './reverbBus';
import { loadSampleBank, isSampleBankLoaded, sampleUrls } from './sampleBanks';

const PIANO_RELEASE = 1.2;
const PIANO_VOLUME = 6;
// Ancien `wet` du piano, repris tel quel en dosage direct/envoi (cf reverbBus).
const PIANO_REVERB_WET = 0.35;

// Le bus de mix est créé une seule fois pour la vie de la page : le jeter et le
// reconstruire empilerait un Gain et un abonné dans playbackMix à chaque relance.
// Son envoi vers l'unique réverbération suit la même règle, il part d'après le
// fader pour que le curseur du mixer baisse aussi la queue.
let mixBus = null;
let reverbReady = false;
let tapSampler = null;
let lessonSampler = null;
let loading = null;

const getMixBus = () => {
  if (!mixBus) {
    mixBus = createMixBus('piano');
    connectReverbSend(mixBus, PIANO_REVERB_WET);
  }
  return mixBus;
};

// Un sampler bâti sur les buffers déjà décodés : Tone enveloppe chaque
// ToneAudioBuffer sans re-télécharger ni re-décoder quoi que ce soit.
const buildSampler = () => {
  const s = new Tone.Sampler({ urls: sampleUrls('piano'), release: PIANO_RELEASE })
    .connect(getMixBus());
  s.volume.value = PIANO_VOLUME;
  return s;
};

const dispose = (node) => {
  try {
    if (node && typeof node.dispose === 'function') node.dispose();
  } catch (e) {
    // déjà libéré
  }
};

// Chargement raté ou trop long : on jette ce qui a été construit ici (jamais le
// bus de mix, jamais les buffers partagés) pour qu'un prochain appel reparte de
// zéro. Les buffers appartiennent à sampleBanks, qui les jette lui-même quand
// le téléchargement échoue et retire la banque de son registre : le prochain
// `loadSampleBank` relance alors le chargement. Contrepartie assumée : un
// téléchargement qui traîne sans jamais échouer n'est pas relancé par le délai,
// on se rattache à la même promesse.
const discardPiano = () => {
  dispose(tapSampler);
  dispose(lessonSampler);
  tapSampler = null;
  lessonSampler = null;
  reverbReady = false;
  loading = null;
};

const getPiano = () => {
  if (!loading) {
    // Le bus et son envoi existent dès ici, avant les samplers.
    getMixBus();
    // Promesse propre à la banque : `Tone.loaded()` attendrait toute la liste
    // globale (nappe, couches du manche) et échouerait pour un téléchargement
    // sans rapport.
    const decoded = loadSampleBank('piano').ready;
    const build = () => {
      reverbReady = true;
      tapSampler = buildSampler();
      lessonSampler = buildSampler();
      return lessonSampler;
    };
    // Même garantie qu'avant : rien ne joue avant que la réverbération soit
    // générée. Elle est désormais partagée, donc une seule promesse. Sous
    // contournement (variantes C/D) elle est déjà résolue, personne n'attend.
    loading = Promise.all([decoded, whenReverbReady()]).then(build);
  }
  return loading;
};

// Kick off loading ahead of the first tap so it is ready when needed.
export const warmUpPiano = () => { getPiano(); };

// Le piano est-il jouable tout de suite ? Synchrone, sans rien déclencher.
export const isPianoReady = () => Boolean(
  isSampleBankLoaded('piano') && reverbReady && lessonSampler
);

// Attend que les échantillons du piano et la réverbération soient prêts. Rejette
// avec un code plutôt que d'attendre sans fin, et jette le piano à moitié
// construit pour qu'un prochain appel relance le chargement : une panne réseau
// reste rattrapable.
export const pianoReady = ({ timeoutMs = 15000 } = {}) => {
  const pending = getPiano();
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const fail = (code) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      discardPiano();
      const err = new Error(code);
      err.code = code;
      reject(err);
    };
    const timer = setTimeout(() => {
      // Délai atteint mais piano prêt entre-temps (attente abandonnée, puis
      // départ par le chemin synchrone) : on résout, on ne jette rien.
      if (isPianoReady()) done(lessonSampler);
      else fail('piano-timeout');
    }, timeoutMs);
    Promise.resolve(pending).then(done, () => fail('piano-load-failed'));
  });
};

// Lecture programmée (lesson player) : attaque et relâche séparées, là où un tap
// du manche utilise triggerAttackRelease. Raison : une note soumise par
// triggerAttackRelease sort des sources suivies dès sa soumission, elle est donc
// inannulable ; une note attaquée reste suivie jusqu'à sa relâche, et releaseAll
// la coupe même si son départ est encore à venir.
// Retourne false tant que les échantillons ne sont pas chargés (le caller réessaie).
export const attackLessonNotes = (noteNames, when) => {
  getPiano();
  if (!isPianoReady()) return false;
  lessonSampler.triggerAttack(noteNames, when);
  return true;
};

// Attention : triggerRelease arrête TOUTES les sources suivies de ces hauteurs et
// vide leur liste. La relâche d'un pas doit donc être posée avant l'attaque du
// pas suivant quand ils partagent une note.
export const releaseLessonNotes = (noteNames, time) => {
  if (!isPianoReady()) return false;
  lessonSampler.triggerRelease(noteNames, time);
  return true;
};

// Coupe tout ce que le lecteur de leçon a attaqué, y compris les notes dont le
// départ est encore dans le futur. Les taps du manche sont sur l'autre sampler,
// ils ne sont pas touchés.
export const cancelLessonNotes = (time) => {
  if (!isPianoReady()) return false;
  lessonSampler.releaseAll(time);
  return true;
};

// Play a single note, e.g. "C#4". Silently no-ops until the samples finish loading.
export const playFretNote = async (noteName) => {
  if (!noteName) return;
  try {
    await Tone.start();
    await getPiano();
    if (tapSampler) tapSampler.triggerAttackRelease(noteName, 1.4);
  } catch (_) {
    // Audio not ready or blocked; ignore
  }
};
